import axios from 'axios'

import { captureUnexpectedError } from '@/utilities/sentry'

const baseURL = process.env.VUE_APP_PREFIX ? `${process.env.VUE_APP_PREFIX}/api` : '/api'

// need plainAxiosRequest to prevent infinite loops in axiosRequest interceptors
export const plainAxiosRequest = axios.create({
  baseURL,
})

const axiosOpts = {
  headers: {
    Accept: 'application/json',
    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
      ? document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      : '',
  },
  baseURL,
}

export const axiosRequest = axios.create(axiosOpts)

export const updateHeaders = token => {
  if (token) {
    axiosRequest.defaults.headers.Authorization = token
    plainAxiosRequest.defaults.headers.Authorization = token
  } else {
    delete axiosRequest.defaults.headers.Authorization
    delete plainAxiosRequest.defaults.headers.Authorization
  }
}

// Concurrent 401s share a single in-flight refresh instead of each firing its own
// /refresh_user call and, on failure, its own sessionExpired dispatch.
let refreshPromise = null
let sessionExpiredDispatched = false

const requestNewAccessToken = () => {
  if (!refreshPromise) {
    sessionExpiredDispatched = false
    refreshPromise = plainAxiosRequest.get('/refresh_user').
      finally(_ => { refreshPromise = null })
  }

  return refreshPromise
}

axiosRequest.interceptors.response.use(
  response => response, // Directly return successful responses.
  async error => {
    const originalRequest = error.config
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark the request as retried to avoid infinite loops.
      originalRequest._retry = true
      try {
        const response = await requestNewAccessToken()
        const accessToken = response.data.access_token
        // Update the authorization header with the new access token.
        updateHeaders(accessToken)
        originalRequest.headers.Authorization = accessToken
        // Retry the original request with the new access token.
        return axiosRequest(originalRequest)
      } catch (refreshError) {
        if (!refreshError.response || refreshError.response.status >= 500) {
          captureUnexpectedError(refreshError)
        }
        updateHeaders()
        window.dispatchEvent(new Event('sessionExpired'))
        // Only force a full (cross-tab) session-expiry logout when the backend has
        // definitively rejected the refresh token. A transient network/server error
        // here shouldn't log the user out of every open tab.
        if (refreshError.response?.status === 403 && !sessionExpiredDispatched) {
          sessionExpiredDispatched = true
          window.dispatchEvent(new Event('sessionExpired'))
        }

        return Promise.reject(refreshError)
      }
    }

    // For all other errors, return the error as is.
    return Promise.reject(error)
  }
)
